import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, BookOpen, Flower2, HeartPulse, Megaphone, Salad, Video } from "lucide-react";

const PHASE = { pre: "Pre-Pregnancy · Beej Sanskar", preg: "Pregnancy · Garbhini Paricharya", post: "Post-Pregnancy · Bal Sanskar" };
const CARDS = [
  { key: "diet", title: "Diet · Ahara", icon: Salad },
  { key: "exercise", title: "Exercise & Yoga", icon: HeartPulse },
  { key: "reading", title: "Reading / Listening", icon: BookOpen },
  { key: "reflection", title: "Reflection & Practice", icon: Flower2 },
];

/**
 * The member's Garbhsanskar month on their own dashboard: this month's four
 * practices, the Samvad sessions they are invited to, and the clinic's
 * announcements. Renders nothing for anyone who has not joined.
 */
export const MyGarbhaCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/garbha/me").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data?.enrolled) return null;

  const p = data.participant;
  return (
    <Card className="border-border" data-testid="my-garbha-card">
      <CardContent className="p-6 lg:p-8 space-y-7">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <h3 className="font-display text-2xl mb-1">My Garbhsanskar month</h3>
            <p className="text-sm text-muted-foreground">
              {PHASE[p.phase]} · Month {p.month}
              {p.trimester > 0 ? ` · Trimester ${p.trimester}` : ""}
            </p>
          </div>
          <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10 capitalize" data-testid="garbha-db-status">
            {data.subscription_status || p.status}
          </Badge>
          <Link
            to="/garbhsanskar"
            className="ml-auto inline-flex items-center gap-1 text-sm text-primary hover:underline"
            data-testid="garbha-db-open-program"
          >
            Open the full programme <ArrowRight size={13} />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 gap-4" data-testid="garbha-db-cards">
          {CARDS.map((c) => (
            <div key={c.key} className="border border-border rounded-xl p-5">
              <c.icon size={18} className="text-secondary mb-2" />
              <div className="font-display text-lg mb-1">{c.title}</div>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{data.stage?.[c.key] || "—"}</p>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div data-testid="garbha-db-samvad">
            <div className="overline mb-3">Samvad sessions for you</div>
            {(data.upcoming_samvad || []).length === 0 && (
              <p className="text-sm text-muted-foreground">No live session scheduled yet.</p>
            )}
            {(data.upcoming_samvad || []).map((s) => (
              <div key={s.id} className="border border-border rounded-xl p-4 mb-3" data-testid={`garbha-db-samvad-${s.id}`}>
                <div className="font-medium text-sm">{s.title}</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(s.scheduled_at).toLocaleString()}{s.topic ? ` · ${s.topic}` : ""}
                </div>
                {s.session_link && (
                  <a
                    href={s.session_link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline mt-2"
                    data-testid={`garbha-db-samvad-link-${s.id}`}
                  >
                    <Video size={13} /> Join the session
                  </a>
                )}
              </div>
            ))}
          </div>

          <div data-testid="garbha-db-announcements">
            <div className="overline mb-3">Announcements</div>
            {(data.announcements || []).length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing new from the clinic.</p>
            )}
            {(data.announcements || []).map((a) => (
              <div key={a.id} className="border-b border-border pb-3 mb-3" data-testid={`garbha-db-announcement-${a.id}`}>
                <div className="flex items-center gap-2">
                  <Megaphone size={13} className="text-secondary" />
                  <span className="font-medium text-sm">{a.title}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{new Date(a.sent_at).toLocaleDateString()}</span>
                </div>
                {a.image && <img src={mediaUrl(a.image)} alt={a.title} className="mt-2 rounded-lg max-h-40 object-cover border border-border" />}
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MyGarbhaCard;
